import { Controller } from "@hotwired/stimulus"

// Debounced place autocomplete against the admin /places/search endpoint
// (Stadia). Renders a suggestions dropdown under the search input; picking a
// suggestion fills the address fields (address line, city, state, postal code)
// with whatever components Stadia provided + dispatches "place-search:selected"
// ({ label, lat, lng }) so the location-picker map can recenter and drop its
// pin (which stays click-adjustable). Country is a combobox and is left for
// the person to pick — a plain `.value =` assignment doesn't drive its
// selection state. State is the region-select's native <select>, whose option
// values are region names (what Stadia returns), so assignment works there
// when the country already matches.
export default class extends Controller {
  static targets = [
    "input",
    "results",
    "addressField",
    "cityField",
    "stateField",
    "postalCodeField",
  ]
  static values = { url: String }

  connect() {
    // ponytail: per-page-load query → results memo; backspacing through a
    // query re-renders instantly instead of re-hitting Stadia
    this.cache = new Map()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
  }

  search() {
    clearTimeout(this.debounceTimer)

    const query = this.inputTarget.value.trim()
    if (query.length < 3) {
      this.clear()
      return
    }

    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300)
  }

  async fetchResults(query) {
    if (this.cache.has(query)) {
      this.renderResults(this.cache.get(query), query)
      return
    }

    this.renderStatus("Searching…")

    try {
      // the url may already carry a query string (private events pass ?conversation=)
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (this.inputTarget.value.trim() !== query) return // stale response
      if (!response.ok) {
        this.renderStatus("Couldn't search right now — type the address below instead.")
        return
      }

      const results = (await response.json()).results || []
      // an empty list may be an upstream hiccup, so only successes are memoized
      if (results.length > 0) this.cache.set(query, results)
      this.renderResults(results, query)
    } catch {
      // autocomplete is a convenience — swallow network errors
      if (this.inputTarget.value.trim() === query) this.clear()
    }
  }

  // The dropdown doubles as the status line, so "still searching" and "found
  // nothing" are never confused with each other or with "nothing happened".
  renderStatus(text) {
    const row = document.createElement("div")
    row.setAttribute("role", "status")
    row.className = "px-4 py-2.5 text-sm text-gray-500 dark:text-neutral-400"
    row.textContent = text
    this.resultsTarget.replaceChildren(row)
    this.resultsTarget.classList.remove("hidden")
  }

  renderResults(results, query) {
    if (results.length === 0) {
      this.renderStatus(`No places found for “${query}” — type the address below instead.`)
      return
    }

    this.resultsTarget.replaceChildren()

    results.forEach((result) => {
      const button = document.createElement("button")
      button.type = "button"
      button.textContent = result.label
      button.className =
        "block w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
      button.addEventListener("click", () => this.select(result))
      this.resultsTarget.appendChild(button)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  select(result) {
    this.fillAddressFields(result)
    this.inputTarget.value = result.label
    this.clear()

    this.dispatch("selected", {
      detail: { label: result.label, lat: parseFloat(result.lat), lng: parseFloat(result.lng) },
    })
  }

  // A selection is authoritative for the fields it touches: every component
  // fills from the picked place, blanking components Stadia didn't provide so
  // stale values from a previous selection can't linger. The address line
  // always has a value (the server falls back to the POI name, then the label).
  fillAddressFields(result) {
    if (this.hasAddressFieldTarget) this.addressFieldTarget.value = result.address || result.label
    if (this.hasCityFieldTarget) this.cityFieldTarget.value = result.city || ""
    if (this.hasStateFieldTarget) this.stateFieldTarget.value = result.state || ""
    if (this.hasPostalCodeFieldTarget) this.postalCodeFieldTarget.value = result.postal_code || ""
  }

  clear() {
    this.resultsTarget.replaceChildren()
    this.resultsTarget.classList.add("hidden")
  }
}
